import { useMemo } from 'react';

interface DataPoint {
  date: string;
  value: number;
}

interface MetricSparklineProps {
  data: DataPoint[];
  color?: string;
  width?: number;
  height?: number;
}

const MetricSparkline = ({
  data,
  color = '#2563eb',
  width = 280,
  height = 60,
}: MetricSparklineProps) => {
  const { path, dots, yMin, yMax } = useMemo(() => {
    if (data.length === 0) return { path: '', dots: [], yMin: 0, yMax: 0 };

    // Sort by date ascending for the line
    const sorted = [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const values = sorted.map((d) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    const padX = 12;
    const padY = 8;
    const chartW = width - padX * 2;
    const chartH = height - padY * 2;

    const points = sorted.map((d, i) => ({
      x: padX + (sorted.length === 1 ? chartW / 2 : (i / (sorted.length - 1)) * chartW),
      y: padY + chartH - ((d.value - min) / range) * chartH,
    }));

    const pathStr = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    return { path: pathStr, dots: points, yMin: min, yMax: max };
  }, [data, width, height]);

  if (data.length === 0) return null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
    >
      {/* Gradient fill under line */}
      <defs>
        <linearGradient id={`sparkFill-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.15} />
          <stop offset="100%" stopColor={color} stopOpacity={0.02} />
        </linearGradient>
      </defs>

      {/* Area fill */}
      {dots.length > 1 && (
        <path
          d={`${path} L ${dots[dots.length - 1].x} ${height - 8} L ${dots[0].x} ${height - 8} Z`}
          fill={`url(#sparkFill-${color.replace('#', '')})`}
        />
      )}

      {/* Line */}
      <path d={path} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />

      {/* Dots */}
      {dots.map((dot, i) => (
        <circle key={i} cx={dot.x} cy={dot.y} r={2.5} fill="white" stroke={color} strokeWidth={1.5} />
      ))}
    </svg>
  );
};

export default MetricSparkline;
